import * as React from "react";
import { useCallback, useContext, useMemo } from "react";
import styled from "styled-components";
import upperCase from "lodash/upperCase";
import Icon from "components/common/Icon";
import { MenuItem } from "components/bootstrap";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import { createHandlerFor, isExternalLinkAction } from "views/components/actions/ActionHandler";
import HoverForHelp from "components/common/HoverForHelp";
import useViewsDispatch from "views/stores/useViewsDispatch";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
const StyledMenuItem = styled(MenuItem)`
  && > a {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;
const ExternalLinkIcon = styled(Icon)(
  ({ theme }) => `
  margin-left: ${theme.spacings.xxs};
`
);
const StyledHoverForHelp = styled((props) => /* @__PURE__ */ React.createElement(HoverForHelp, { ...props }))`
  margin-left: 5px;
`;
const ActionTitle = ({ action, handlerArgs }) => {
  if (action.help) {
    const help = action.help(handlerArgs);
    if (help) {
      const { title, description } = help;
      return /* @__PURE__ */ React.createElement(React.Fragment, null, action.title, /* @__PURE__ */ React.createElement(StyledHoverForHelp, { title, testId: "menu-item-help" }, description));
    }
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, action.title);
};
const ExternalLinkItem = ({ action, disabled, field, handlerArgs, onMenuToggle, type }) => {
  const { unsetWidgetFocusing } = useContext(WidgetFocusContext);
  const linkProps = {
    href: action.linkTarget(handlerArgs),
    target: "_blank",
    rel: "noopener noreferrer"
  };
  const onSelect = useCallback(() => {
    const { resetFocus = false } = action;
    if (resetFocus) {
      unsetWidgetFocusing();
    }
    onMenuToggle();
  }, [action, onMenuToggle, unsetWidgetFocusing]);
  return /* @__PURE__ */ React.createElement(StyledMenuItem, { disabled, eventKey: { action: type, field }, onSelect, ...linkProps }, /* @__PURE__ */ React.createElement(ActionTitle, { action, handlerArgs }), /* @__PURE__ */ React.createElement(ExternalLinkIcon, { name: "open_in_new" }));
};
const ActionHandlerItem = ({
  disabled,
  action,
  handlerArgs,
  setOverflowingComponents,
  overflowingComponents,
  type,
  onMenuToggle
}) => {
  const { unsetWidgetFocusing } = useContext(WidgetFocusContext);
  const dispatch = useViewsDispatch();
  const sendTelemetry = useSendTelemetry();
  const setActionComponents = useCallback(
    (fn) => {
      setOverflowingComponents(fn(overflowingComponents));
    },
    [overflowingComponents, setOverflowingComponents]
  );
  const handler = useMemo(
    () => createHandlerFor(dispatch, action, setActionComponents),
    [action, dispatch, setActionComponents]
  );
  const onSelect = useCallback(() => {
    const { resetFocus = false, title } = action;
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FIELD_VALUE_ACTION[upperCase(title).replace(/\s|\//g, "_")], {
      app_section: "search-field-value",
      event_details: {}
    });
    if (resetFocus) {
      unsetWidgetFocusing();
    }
    onMenuToggle();
    handler(handlerArgs);
  }, [action, handler, handlerArgs, onMenuToggle, sendTelemetry, unsetWidgetFocusing]);
  const { field } = handlerArgs;
  return /* @__PURE__ */ React.createElement(StyledMenuItem, { disabled, eventKey: { action: type, field }, onSelect }, /* @__PURE__ */ React.createElement(ActionTitle, { action, handlerArgs }));
};
const ActionMenuItem = ({
  action,
  handlerArgs,
  setOverflowingComponents,
  overflowingComponents,
  type,
  onMenuToggle
}) => {
  const { isEnabled = () => true } = action;
  const dispatch = useViewsDispatch();
  const actionDisabled = dispatch((_dispatch, getState) => !isEnabled(handlerArgs, getState));
  const { field } = handlerArgs;
  if (isExternalLinkAction(action)) {
    return /* @__PURE__ */ React.createElement(
      ExternalLinkItem,
      {
        action,
        disabled: actionDisabled,
        field,
        handlerArgs,
        onMenuToggle,
        type
      }
    );
  }
  return /* @__PURE__ */ React.createElement(
    ActionHandlerItem,
    {
      action,
      disabled: actionDisabled,
      field,
      handlerArgs,
      onMenuToggle,
      overflowingComponents,
      setOverflowingComponents,
      type
    }
  );
};
export default ActionMenuItem;
